package gr.tech.pdfgenerator.styles

import com.varabyte.kobweb.compose.css.FontStyle
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.ListStyleType
import com.varabyte.kobweb.compose.css.OverflowWrap
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.css.VerticalAlign
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import gr.tech.pdfgenerator.util.CONSTANTS.FONT_FAMILY_TN
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.px

/* Table styles */
val t3TableColumnStyle = CssStyle.base {
    Modifier
        .padding(top = 4.px, bottom = 4.px, left = 10.px)
        .textAlign(TextAlign.Center)
        .verticalAlign(VerticalAlign.Middle)
        .overflowWrap(OverflowWrap.Anywhere)
        .border(1.px, LineStyle.Solid, Colors.Black)
}

val t3TableHeaderColumnStyle = CssStyle.base {
    Modifier
        .padding(topBottom = 4.px)
        .textAlign(TextAlign.Center)
        .verticalAlign(VerticalAlign.Middle)
        .overflowWrap(OverflowWrap.Anywhere)
        .border(1.px, LineStyle.Solid, Colors.Black)
}

val t3HeaderBackground = CssStyle.base {
    Modifier
        .backgroundColor(Colors.LightGray)
}

val t3TableHeaderStyle = CssStyle.base {
    Modifier
        .fontSize(11.px)
        .fontFamily(FONT_FAMILY_TN)
        .fontWeight(FontWeight.Bold)
        .fillMaxWidth()
        .textAlign(TextAlign.Center)
}

val t3FeeTableTextStyle = CssStyle.base {
    Modifier
        .fontSize(11.px)
        .fontWeight(FontWeight.Bold)
        .fontFamily(FONT_FAMILY_TN)
        .fillMaxWidth()
        .textAlign(TextAlign.Start)
}

val t3QEESMTableTextStyle = CssStyle.base {
    Modifier
        .fontSize(9.px)
        .fontWeight(FontWeight.Normal)
        .fontFamily(FONT_FAMILY_TN)
        .fillMaxWidth()
        .textAlign(TextAlign.Start)
}

/* General Page Styles */
val t3DocumentTitleFontStyle = CssStyle {
    base {
        Modifier
            .fontSize(12.px)
            .lineHeight(1.25)
            .fontWeight(FontWeight.Medium)
            .fontStyle(FontStyle.Italic)
            .fontFamily(FONT_FAMILY_TN)
    }
}

val t3DocumentSectionFontStyle = CssStyle {
    base {
        Modifier
            .fontSize(24.px)
            .lineHeight(1.25)
            .fontWeight(FontWeight.Bold)
            .fontFamily(FONT_FAMILY_TN)
            .color(Theme.DarkGreen.rgb)
    }
}

/* Page One Styles */

val t3SummaryRowContainerStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .margin(top = 16.px)
            .padding(leftRight = 32.px, topBottom = 16.px)
            .backgroundColor(Theme.PDFNearBackground.rgb)
    }
}

val t3SignBelowListStyle = CssStyle {
    base {
        Modifier
            .listStyle(ListStyleType.None)
            .margin(leftRight = 16.px)
            .gap(32.px)
    }
}

val t3SignBelowListItemStyle = CssStyle {
    base {
        Modifier
            .display(DisplayStyle.Flex)
            .flexDirection(FlexDirection.Row)
            .alignItems(AlignItems.Start)
            .margin(top = 4.px)
    }
}

val t3SignBoxStyle = CssStyle {
    base {
        Modifier
            .width(16.px)
            .height(16.px)
            .border(1.px, LineStyle.Solid, Colors.Black)
            .borderRadius(2.px)
            .margin(top = 2.px, right = 16.px)
    }
}

val t3SignBoxParagraphStyle = CssStyle {
    base {
        Modifier
            .fontSize(13.3.px)
    }
}

/* Page Two Lists */

val t3TightParagraphStyle = CssStyle {
    base {
        Modifier
            .color(Colors.Black)
            .fontFamily(FONT_FAMILY_TN)
            .fontSize(12.px)
            .lineHeight(1.10)
            .textAlign(TextAlign.Start)
            .fillMaxWidth()
            .margin(0.px)
    }
}

/*
    Cannot use ::marker, as its not supported in the HTML2PDF render tech.
    Opt'ing to use more compatible ::before, however its a bit more finicky with styling.
 */
val t3TermsListStyle = CssStyle {
    base {
        Modifier
            .margin(top = 0.px)
            .padding(0.px)
            .listStyle(ListStyleType.None)
            .styleModifier {
                property("counter-reset", "item")
            }
    }
    cssRule("li") {
        Modifier
            .position(Position.Relative)
            .padding(left = 24.px)
            .styleModifier {
                property("counter-increment", "item")
            }
    }
    cssRule("li::before") {
        Modifier
            .fontSize(11.px)
            .fontFamily(FONT_FAMILY_TN)
            .position(Position.Absolute)
            .left(0.px)
            .styleModifier {
                property("content", "counter(item) \") \"")
            }
    }
}

/*
    Cannot use ::marker, as its not supported in the HTML2PDF render tech.
    Opt'ing to use more compatible ::before, however its a bit more finicky with styling.
 */
val t3TermsListAlphaStyle = CssStyle {
    base {
        Modifier
            .margin(top = 0.px)
            .padding(0.px)
            .listStyle(ListStyleType.None)
            .styleModifier {
                property("counter-reset", "item")
            }
    }
    cssRule("li") {
        Modifier
            .position(Position.Relative)
            .padding(left = 24.px)
            .styleModifier {
                property("counter-increment", "item")
            }
    }
    cssRule("li::before") {
        Modifier
            .fontSize(11.px)
            .fontFamily(FONT_FAMILY_TN)
            .position(Position.Absolute)
            .left(0.px)
            .styleModifier {
                property("content", "counter(item, lower-alpha) ') '")
            }
    }
}


val t3TermsListItemStyle = CssStyle {
    base {
        Modifier
            .margin(top = 2.px)
            .padding(0.px, left = 6.px)
    }
}
